import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { useAuth } from "@/context/auth";
import { useI18n } from "@/i18n";
import { useTheme } from "@/context/theme";
import { Card, Spinner, Btn, fmtMoney } from "@/components/ui-rp";
import { Download } from "lucide-react";

export const MonthlyReport = () => {
  const { t, lang } = useI18n();
  const { client, user } = useAuth();
  const { theme } = useTheme();
  const [rows, setRows] = useState(null);
  useEffect(() => { client.get("/tenant/reports/monthly", { params: { months: 6 } }).then(({ data }) => setRows(data)).catch(() => setRows([])); }, [client]);
  const dark = theme === "dark";
  const label = (m) => new Date(`${m}-02`).toLocaleDateString(lang === "es" ? "es-PA" : "en-US", { month: "short" });
  const download = async (fmt) => {
    const { data } = await client.get(`/tenant/reports/monthly.${fmt}`, { params: { months: 6 }, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `reporte-mensual.${fmt}`; a.click();
  };
  return (
    <Card testId="monthly-report-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h2 className="font-heading text-base md:text-lg font-semibold">{t("report.title")} <span className="text-xs font-normal text-muted-foreground">· 6 {t("report.months")}</span></h2>
        {user.role !== "cashier" && (
          <div className="flex gap-2">
            <Btn variant="outline" className="h-9 px-3 text-xs" onClick={() => download("xlsx")} testId="report-download-xlsx"><Download className="w-3.5 h-3.5" />{t("report.excel")}</Btn>
            <Btn variant="outline" className="h-9 px-3 text-xs" onClick={() => download("pdf")} testId="report-download-pdf"><Download className="w-3.5 h-3.5" />{t("report.pdf")}</Btn>
          </div>
        )}
      </div>
      {!rows ? <Spinner /> : (
        <div className="h-64 -ml-4" data-testid="monthly-report-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows.map((r) => ({ ...r, name: label(r.month) }))} barGap={2}>
              <CartesianGrid vertical={false} stroke={dark ? "#1f2937" : "#e2e8f0"} />
              <XAxis dataKey="name" tick={{ fill: dark ? "#94a3b8" : "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: dark ? "#94a3b8" : "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} width={56} />
              <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: dark ? "#111827" : "#fff", border: `1px solid ${dark ? "#1f2937" : "#e2e8f0"}`, borderRadius: 12, color: dark ? "#f8fafc" : "#0b0f19" }} cursor={{ fill: dark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="sales" name={t("report.sales")} fill="#0fe814" radius={[6, 6, 0, 0]} />
              <Bar dataKey="fees" name={t("report.fees")} fill={dark ? "#f59e0b" : "#d97706"} radius={[6, 6, 0, 0]} />
              <Bar dataKey="cashouts" name={t("report.cashouts")} fill={dark ? "#38bdf8" : "#0284c7"} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};
